/**
 * Tenant Setup Step - First step: provisions tenant artifacts
 * Wraps the bootstrap API logic (formerly BootstrapOnboarding.tsx)
 */

import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { getBootstrapStatus, runOnboarding } from '../../services/bootstrapService';
import type { BootstrapStatusResponse } from '../../../../shared/dto/bootstrap';

interface TenantSetupProps {
    /** Called when tenant setup is complete and the wizard should advance */
    onNext: () => void;
}

export default function TenantSetup({ onNext }: TenantSetupProps) {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [status, setStatus] = useState<BootstrapStatusResponse | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [running, setRunning] = useState(false);

    // Guard against StrictMode double-fire and remount cycles
    const initRef = useRef(false);

    useEffect(() => {
        if (initRef.current) return;
        initRef.current = true;
        loadStatus();
    }, []);

    const loadStatus = async () => {
        try {
            setLoading(true);
            setError(null);
            const result = await getBootstrapStatus();
            setStatus(result);

            // Non-eligible users → permissions error
            if (!result.eligibleToOnboard && !result.onboardingInProgress) {
                navigate('/permissions-error', { replace: true });
                return;
            }
        } catch (err: any) {
            setError(err.message || 'Failed to load bootstrap status');
        } finally {
            setLoading(false);
        }
    };

    const handleProvision = async () => {
        if (!status?.eligibleToOnboard) return;
        try {
            setRunning(true);
            setError(null);
            console.log('[TenantSetup] Starting provisioning...');
            const result = await runOnboarding();
            console.log('[TenantSetup] Provisioning result:', result);
            console.log('[TenantSetup] Calling onNext() to proceed to wizard');
            onNext();
        } catch (err: any) {
            console.error('[TenantSetup] Provisioning failed:', err);
            setError(err.message || 'Tenant provisioning failed');
        } finally {
            setRunning(false);
        }
    };

    if (loading) {
        return (
            <div className="bg-white shadow sm:rounded-lg p-8 text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2ba6bb] mx-auto" />
                <p className="mt-4 text-gray-600">Checking tenant status…</p>
            </div>
        );
    }

    return (
        <div className="bg-white shadow sm:rounded-lg">
            <div className="px-4 py-5 sm:p-6">
                <h2 className="text-2xl font-semibold text-gray-900 mb-6">Initialize Your Tenant</h2>

                {error && (
                    <div className="mb-6 rounded-md bg-red-50 p-4">
                        <div className="flex">
                            <ExclamationTriangleIcon className="h-5 w-5 text-red-400 mr-3 flex-shrink-0" />
                            <div>
                                <p className="text-sm text-red-700">{error}</p>
                                <button onClick={loadStatus} className="mt-2 text-sm text-red-700 underline">
                                    Retry
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {status && !status.setupComplete && (
                    <div className="space-y-6">
                        <div className="space-y-3 text-gray-700">
                            <div>
                                <strong>Tenant ID:</strong> {status.tenantId}
                            </div>
                            {status.onboardingInProgress && (
                                <div className="flex items-start text-orange-600">
                                    <ExclamationTriangleIcon className="h-5 w-5 mr-2 mt-0.5" />
                                    <span>Provisioning is currently in progress…</span>
                                </div>
                            )}
                        </div>

                        <div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">Eligibility</h3>
                            <ul className="space-y-2">
                                {status.reasons.map((reason, idx) => (
                                    <li key={idx} className="flex items-start text-gray-700">
                                        <span className={`mr-2 font-bold ${status.eligibleToOnboard ? 'text-[#2ba6bb]' : 'text-gray-400'}`}>
                                            {status.eligibleToOnboard ? '✓' : '○'}
                                        </span>
                                        {reason}
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {status.eligibleToOnboard && !status.onboardingInProgress && (
                            <button
                                onClick={handleProvision}
                                disabled={running}
                                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#2ba6bb] hover:bg-[#228a9d] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#2ba6bb] disabled:bg-gray-300 disabled:cursor-not-allowed"
                            >
                                {running ? (
                                    <>
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                                        Provisioning…
                                    </>
                                ) : (
                                    'Initialize Tenant'
                                )}
                            </button>
                        )}

                        {status.onboardingInProgress && (
                            <button
                                onClick={loadStatus}
                                className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                            >
                                Refresh Status
                            </button>
                        )}
                    </div>
                )}

                {status?.setupComplete && (
                    <div className="text-center space-y-4">
                        <CheckCircleIcon className="h-16 w-16 text-[#2ba6bb] mx-auto" />
                        <p className="text-gray-700">Tenant is provisioned. Proceeding…</p>
                    </div>
                )}
            </div>
        </div>
    );
}
